'use client'

import { useState, ReactNode, MouseEvent, useEffect } from 'react'
import { Button, Cell, Dialog, Divider, Icon, Spacer, Text } from '@vinted/web-ui'

import { ExclamationTriangleFilled24, Lock24, EyeCrossed24 } from '@vinted/monochrome-icons'

import {
  TransactionSide,
  escrowWarningViewEvent,
  clickEvent,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { FaqEntryType } from '@marketplace-web/help-center/help-center-faq-entry-url-data'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'

type Props = {
  currentUserId: number | undefined
  userSide?: TransactionSide | null
  conversationId: string | number | undefined
  messageReplyId: number | string
  messageSenderId: string
  messageRecipientId: string | number | undefined
  transactionId: number | undefined
  isCurrentAuthor: boolean
}

const EscrowWarningMessage = ({
  currentUserId,
  userSide,
  messageReplyId,
  conversationId,
  messageSenderId,
  messageRecipientId,
  transactionId,
  isCurrentAuthor,
}: Props) => {
  const [isDialogVisible, setIsDialogVisible] = useState(false)

  const translate = useTranslate('conversation.conversation_info.off_escrow_warning')

  const { track } = useTracking()

  const messageConversationSide = isCurrentAuthor ? 'sender' : 'receiver'
  const isSeller = userSide === TransactionSide.Seller
  const isBuyer = userSide === TransactionSide.Buyer

  const isEscrowWarningMessageVisible = isSeller || isBuyer

  useEffect(() => {
    if (!isEscrowWarningMessageVisible) return

    track(
      escrowWarningViewEvent({
        userId: currentUserId ? currentUserId.toString() : undefined,
        userSide,
        msgThreadId: conversationId ? conversationId.toString() : undefined,
        msgReplyId: messageReplyId.toString(),
        userIdRecipient: messageRecipientId ? messageRecipientId.toString() : undefined,
        userIdSender: messageSenderId,
        msgConversationSide: messageConversationSide,
        transactionId: transactionId ? transactionId.toString() : undefined,
      }),
    )
  }, [
    isEscrowWarningMessageVisible,
    messageConversationSide,
    conversationId,
    currentUserId,
    messageRecipientId,
    messageReplyId,
    messageSenderId,
    track,
    userSide,
    transactionId,
  ])

  const handleDialogLinkClick = () => {
    track(
      clickEvent({
        target: 'escrow_warning_modal_link',
        targetDetails: JSON.stringify({
          user_side: userSide,
          msg_thread_id: conversationId,
          msg_reply_id: messageReplyId.toString(),
          user_id_sender: messageSenderId,
          msg_conversation_side: messageConversationSide,
        }),
      }),
    )
  }

  const handleDialogOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    track(
      clickEvent({
        target: 'escrow_warning_link',
        targetDetails: JSON.stringify({
          user_side: userSide,
          msg_thread_id: conversationId,
          msg_reply_id: messageReplyId.toString(),
          user_id_sender: messageSenderId,
          msg_conversation_side: messageConversationSide,
        }),
      }),
    )

    setIsDialogVisible(true)
  }

  const handleDialogClose = () => {
    setIsDialogVisible(false)

    track(
      clickEvent({
        target: 'escrow_warning_modal_close',
        targetDetails: JSON.stringify({
          user_side: userSide,
          msg_thread_id: conversationId,
          msg_reply_id: messageReplyId.toString(),
          user_id_sender: messageSenderId,
          msg_conversation_side: messageConversationSide,
        }),
      }),
    )
  }

  const renderMessage = () => {
    const messageKey = isSeller ? 'seller.message' : 'buyer.message'

    return (
      <Text
        as="p"
        text={translate(messageKey, {
          'learn-more': (chunks: Array<ReactNode>) => (
            <button key="learn-more" type="button" onClick={handleDialogOpen}>
              <Text as="span" type={Text.Type.Caption} text={chunks} clickable />
            </button>
          ),
        })}
        type={Text.Type.Caption}
        theme="muted"
      />
    )
  }

  const renderFutureSalesCell = () => (
    <Cell
      styling={Cell.Styling.Tight}
      prefix={<Icon name={EyeCrossed24} />}
      title={translate('seller.dialog.future_sales.title')}
      body={
        <>
          <Spacer size={Spacer.Size.Small} />
          <Text
            as="p"
            type={Text.Type.Subtitle}
            text={translate('seller.dialog.future_sales.description', {
              'learn-more-link': (chunks: Array<ReactNode>) => (
                <HelpCenterFaqEntryUrl
                  key="learn-more-link"
                  type={FaqEntryType.SafetyGuidelines}
                  render={faqUrl => (
                    <a
                      href={faqUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={handleDialogLinkClick}
                    >
                      {chunks}
                    </a>
                  )}
                />
              ),
            })}
          />
        </>
      }
    />
  )

  const renderPurchaseProtectionCell = () => (
    <Cell
      styling={Cell.Styling.Tight}
      prefix={<Icon name={Lock24} />}
      title={translate('buyer.dialog.purchase_protection.title')}
      body={
        <>
          <Spacer size={Spacer.Size.Small} />
          <Text
            as="p"
            type={Text.Type.Subtitle}
            text={translate('buyer.dialog.purchase_protection.description', {
              'buyer-protection-link': (chunks: Array<ReactNode>) => (
                <HelpCenterFaqEntryUrl
                  key="buyer-protection-link"
                  type={FaqEntryType.BuyerProtection}
                  render={faqUrl => (
                    <a
                      href={faqUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={handleDialogLinkClick}
                    >
                      {chunks}
                    </a>
                  )}
                />
              ),
            })}
          />
        </>
      }
    />
  )

  const renderDialogContent = () => {
    const messageKeyPrefix = isSeller ? 'seller.dialog' : 'buyer.dialog'

    return (
      <>
        <Spacer size={Spacer.Size.X2Large} />
        <Text as="h2" type={Text.Type.Heading} text={translate(`${messageKeyPrefix}.title`)} />
        <Spacer size={Spacer.Size.XLarge} />
        <Cell
          styling={Cell.Styling.Tight}
          prefix={<Icon name={ExclamationTriangleFilled24} />}
          title={translate(`${messageKeyPrefix}.personal_data.title`)}
          body={
            <>
              <Spacer size={Spacer.Size.Small} />
              <Text
                as="p"
                type={Text.Type.Subtitle}
                text={translate(`${messageKeyPrefix}.personal_data.description`)}
              />
            </>
          }
        />
        <Spacer size={Spacer.Size.Large} />
        {isSeller ? renderFutureSalesCell() : renderPurchaseProtectionCell()}
        <Spacer size={Spacer.Size.XLarge} />
        <Text
          as="p"
          type={Text.Type.Subtitle}
          text={translate(`${messageKeyPrefix}.footer_note`)}
        />
        <Spacer size={Spacer.Size.X2Large} />
        <Button
          type={Button.Type.Button}
          styling={Button.Styling.Filled}
          text={translate(`${messageKeyPrefix}.got_it`)}
          onClick={handleDialogClose}
        />
      </>
    )
  }

  if (!isEscrowWarningMessageVisible) return null

  return (
    <>
      <Dialog
        show={isDialogVisible}
        body={renderDialogContent()}
        defaultCallback={handleDialogClose}
        contentDimensions={{ maxWidth: '343px' }}
        enableCloseButton
      />
      <Spacer />
      <Divider />
      <Spacer />
      {renderMessage()}
    </>
  )
}

export default EscrowWarningMessage
